import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { API_BASE } from './apiConfig';

// Token storage key for localStorage
const TOKEN_KEY = 'enablegames_auth_token';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // The old Sessions page cached each account's session list (patients' names, emails,
  // scores) as userData_<id>, and browsers still hold those copies. On a shared computer
  // the next person must not find them: they go whenever the sign-in does (security
  // re-audit 2026-10-16, CR-4).
  const forgetUserData = () => {
    try {
      Object.keys(localStorage).filter((k) => k.startsWith('userData_')).forEach((k) => localStorage.removeItem(k));
    } catch (e) { /* blocked storage: nothing was kept either */ }
  };

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      try {
        // Decode the JWT payload to extract user info
        const payload = JSON.parse(atob(storedToken.split('.')[1]));

        // Check if token is expired (if exp field exists)
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          // Token expired, remove it
          localStorage.removeItem(TOKEN_KEY);
          forgetUserData();
          setIsInitialized(true);
          return;
        }

        // Restore user from token. An experience account's token says so (kind); its end
        // date comes from GET /api/users/:id below.
        const user = {
          user_id: payload.user_id || payload.id || payload.userId,
          email: payload.email,
          role: payload.role,
          name: payload.name,
          kind: payload.kind === 'experience' ? 'experience' : 'standard',
          experienceEndsAt: null
        };

        setUser(user);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Failed to restore auth from localStorage:', error);
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    setIsInitialized(true);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    forgetUserData();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      // Surface the server's reason: 400 {message} ('Email or Username Not Registered',
      // 'Incorrect Password'), 400 {verified:false, message:'Email Not Verified'}, 429 {error}.
      let data = null;
      try { data = await response.json(); } catch { /* no JSON body */ }
      const err = new Error((data && (data.message || data.error)) || 'Login failed');
      err.status = response.status;
      err.data = data;
      throw err;
    }

    const data = await response.json();

    // Store token in localStorage for persistence
    forgetUserData();   // whatever an earlier account left behind
    localStorage.setItem(TOKEN_KEY, data.token);

    // Decode the JWT payload to extract user info (user_id, role, name)
    const payload = JSON.parse(atob(data.token.split('.')[1]));

    // Handle different possible field names from JWT
    // kind and experience_ends_at come with every sign-in answer (an experience account
    // has an end; see accountFacts.js)
    const experience = data.kind === 'experience' || payload.kind === 'experience';
    const user = {
      user_id: payload.user_id || payload.id || payload.userId,
      email: payload.email,
      role: payload.role,
      name: payload.name,
      kind: experience ? 'experience' : 'standard',
      experienceEndsAt: experience && Number.isFinite(data.experience_ends_at) ? data.experience_ends_at : null
    };

    setUser(user);
    setIsAuthenticated(true);
    return { ...data, user };
  }, []);

  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (response.status === 401) {
      logout();
    }

    return response;
  }, [logout]);

  // An experience account restored from its token knows its kind but not its end:
  // GET /api/users/:own_id carries experience_ends_at.
  const needsEnd = user?.kind === 'experience' && user.experienceEndsAt == null ? user.user_id : null;
  useEffect(() => {
    if (!needsEnd) return undefined;
    let live = true;
    authFetch(`${API_BASE}/api/users/${encodeURIComponent(needsEnd)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!live || !body || !Number.isFinite(body.experience_ends_at)) return;
        setUser((u) => (u && u.user_id === needsEnd ? { ...u, experienceEndsAt: body.experience_ends_at } : u));
      })
      .catch(() => { /* the label shows without its date */ });
    return () => { live = false; };
  }, [needsEnd, authFetch]);

  const value = { user, isAuthenticated, isInitialized, login, logout, authFetch };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
